import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import isEmpty from "lodash/isEmpty";
import { showSuccessToast } from "@stadium/services/react-toastify";
import pick from "lodash/pick";

import { emptyCart } from "@actions/order";
import { updateCart, updateCartOrder } from "@slices/cart";

import { EMPTY_CART_SUCCESS_TOAST_ID } from "@constants/toast-ids";

const ORDER_ATTRIBUTES_TO_BE_PRESERVED = ["pricelessItemCount"];

const ZONEWISE_EMPTY_CART_TOAST = "Item(s) have successfully been removed from the bag.";
const DEFAULT_EMPTY_CART_TOAST = "All the items have been removed from your bag.";

const useEmptyCart = ({ successCb = null, showToast = true } = {}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const postEmptyCartSuccess = useCallback(
    ({ order, toastMessage = "" } = {}) => {
      const message = toastMessage || ZONEWISE_EMPTY_CART_TOAST;
      setLoading(false);
      if (successCb) successCb();
      const pickedAttributes = pick(order, ORDER_ATTRIBUTES_TO_BE_PRESERVED);
      dispatch(updateCart(order));

      dispatch(updateCartOrder(pickedAttributes));

      showToast &&
        showSuccessToast({
          message: message,
          config: { toastId: EMPTY_CART_SUCCESS_TOAST_ID },
          title: "Success!",
        });
      return order;
    },
    [dispatch],
  );

  const sendEmptyCartRequest = useCallback(
    (orderId, payload = {}) => {
      setLoading(true);
      return dispatch(emptyCart(orderId, payload))
        .then((order) => {
          const isPayloadEmpty = isEmpty(payload);
          const toastMessage = isPayloadEmpty
            ? DEFAULT_EMPTY_CART_TOAST
            : ZONEWISE_EMPTY_CART_TOAST;
          const postSuccessParams = { order, toastMessage };
          return postEmptyCartSuccess(postSuccessParams);
        })
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    },
    [dispatch, postEmptyCartSuccess],
  );

  return {
    loading,
    emptyCart: sendEmptyCartRequest,
    emptyLocalCart: postEmptyCartSuccess,
  };
};

export default useEmptyCart;
